import React from 'react';
import type { DiagramPage, SequenceParticipant, ThemePalette } from '../../types';
import { NODE_TYPES } from '../../constants';

const PARTICIPANT_TYPES = [
  'service',
  'database',
  'person',
  'api',
  'queue',
  'system',
  'external',
] as const;

const COLOR_PRESETS: ReadonlyArray<string> = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#06b6d4',
  '#ef4444',
  '#8b5cf6',
];

export interface ParticipantInfoPanelProps {
  palette: Pick<ThemePalette, 'panelBg' | 'panelBorder' | 'panelText' | 'panelSubText'>;
  glass: React.CSSProperties;
  participant: SequenceParticipant;
  pages: DiagramPage[];
  seqLifelineLength: number;
  setSeqLifelineLength: (value: number) => void;
  onClose: () => void;
  onUpdateParticipant: (id: string, updates: Partial<SequenceParticipant>) => void;
  onRemoveParticipant: (id: string) => void;
  onSwitchPage: (pageId: string) => void;
  onRequestLinkPage: (participantId: string) => void;
  /** Take an undo snapshot before any mutation. */
  snapshotBeforeMutation: () => void;
}

/**
 * Read/edit panel for a sequence-diagram `SequenceParticipant`.
 *
 * Lets the user change the participant kind, lifeline length, name, description,
 * color, links, linked page portal, and delete the participant.
 */
export const ParticipantInfoPanel: React.FC<ParticipantInfoPanelProps> = ({
  palette,
  glass,
  participant: infoP,
  pages,
  seqLifelineLength,
  setSeqLifelineLength,
  onClose,
  onUpdateParticipant,
  onRemoveParticipant,
  onSwitchPage,
  onRequestLinkPage,
  snapshotBeforeMutation,
}) => {
  return (
    <div
      className="absolute right-4 top-16 bottom-16 z-50 w-80 flex flex-col rounded-2xl shadow-2xl overflow-hidden border backdrop-blur-md"
      style={glass}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: palette.panelBorder }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: infoP.color }}
          />
          <span
            className="text-sm font-semibold truncate"
            style={{ color: palette.panelText }}
          >
            {infoP.label}
          </span>
          {infoP.type && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0"
              style={{ background: infoP.color + '20', color: infoP.color }}
            >
              {infoP.type}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="transition-colors ml-2 flex-shrink-0"
          style={{ color: palette.panelSubText }}
        >
          ✕
        </button>
      </div>
      <div key={infoP.id} className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Type
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{
              color: palette.panelText,
              borderColor: palette.panelBorder,
              background: palette.panelBg,
            }}
            value={infoP.type}
            onChange={(e) => onUpdateParticipant(infoP.id, { type: e.target.value })}
          >
            <option value="">No type</option>
            {PARTICIPANT_TYPES.map((t) => (
              <option key={t} value={t}>
                {NODE_TYPES[t as keyof typeof NODE_TYPES]?.label ?? t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Vertical Space
          </label>
          <input
            type="range"
            min={260}
            max={5000}
            step={20}
            value={seqLifelineLength}
            onChange={(e) => setSeqLifelineLength(Number(e.target.value))}
            className="w-full"
          />
          <div className="mt-1 text-[10px]" style={{ color: palette.panelSubText }}>
            Controls the distance between the top and bottom participant headers on this page.
          </div>
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Name
          </label>
          <input
            type="text"
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            value={infoP.label}
            onChange={(e) => onUpdateParticipant(infoP.id, { label: e.target.value })}
          />
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Description
          </label>
          <textarea
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            rows={3}
            placeholder="Describe this participant..."
            value={infoP.description ?? ''}
            onChange={(e) =>
              onUpdateParticipant(infoP.id, { description: e.target.value || undefined })
            }
          />
        </div>

        <div>
          <label
            className="block text-xs uppercase tracking-wider mb-1.5"
            style={{ color: palette.panelSubText }}
          >
            Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
              value={infoP.color}
              onChange={(e) => onUpdateParticipant(infoP.id, { color: e.target.value })}
            />
            <span className="text-xs font-mono" style={{ color: palette.panelSubText }}>
              {infoP.color}
            </span>
            <div className="flex gap-1 ml-auto">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  title={c}
                  className="w-4 h-4 rounded-full ring-1 ring-white/20 transition-transform hover:scale-110"
                  style={{ background: c }}
                  onClick={() => onUpdateParticipant(infoP.id, { color: c })}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              className="text-xs uppercase tracking-wider"
              style={{ color: palette.panelSubText }}
            >
              Links
            </label>
            <button
              onClick={() =>
                onUpdateParticipant(infoP.id, {
                  links: [...(infoP.links ?? []), { label: '', url: '' }],
                })
              }
              className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-white/5"
              style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
            >
              + Add
            </button>
          </div>
          {(infoP.links ?? []).map((lnk, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                type="text"
                placeholder="Label"
                className="flex-1 bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                value={lnk.label}
                onChange={(e) =>
                  onUpdateParticipant(infoP.id, {
                    links: (infoP.links ?? []).map((l, idx) =>
                      idx === i ? { ...l, label: e.target.value } : l,
                    ),
                  })
                }
              />
              <input
                type="text"
                placeholder="URL"
                className="flex-1 bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                value={lnk.url}
                onChange={(e) =>
                  onUpdateParticipant(infoP.id, {
                    links: (infoP.links ?? []).map((l, idx) =>
                      idx === i ? { ...l, url: e.target.value } : l,
                    ),
                  })
                }
              />
              <button
                onClick={() =>
                  onUpdateParticipant(infoP.id, {
                    links: (infoP.links ?? []).filter((_, idx) => idx !== i),
                  })
                }
                className="text-xs text-red-400 hover:text-red-300 px-1"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* Linked Page */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              className="text-xs uppercase tracking-wider"
              style={{ color: palette.panelSubText }}
            >
              Linked Page
            </label>
          </div>
          {infoP.linkedPageId ? (
            (() => {
              const lp = pages.find((p) => p.id === infoP.linkedPageId);
              return (
                <div
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg border"
                  style={{ borderColor: palette.panelBorder }}
                >
                  <span className="text-xs flex-1 truncate" style={{ color: '#818cf8' }}>
                    ⊞ {lp?.name ?? 'Unknown page'}
                  </span>
                  <button
                    onClick={() => onSwitchPage(infoP.linkedPageId!)}
                    className="text-[10px] px-1.5 py-0.5 rounded transition-colors hover:bg-white/10"
                    style={{ color: '#818cf8' }}
                  >
                    Go →
                  </button>
                  <button
                    onClick={() => {
                      snapshotBeforeMutation();
                      onUpdateParticipant(infoP.id, { linkedPageId: undefined });
                    }}
                    className="text-[10px] text-red-400 hover:text-red-300 px-1"
                  >
                    ✕
                  </button>
                </div>
              );
            })()
          ) : (
            <button
              onClick={() => onRequestLinkPage(infoP.id)}
              className="w-full px-3 py-1.5 rounded-lg text-xs border transition-colors hover:bg-white/5"
              style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
            >
              ⊞ Link to a page…
            </button>
          )}
        </div>

        <button
          onClick={() => {
            snapshotBeforeMutation();
            onRemoveParticipant(infoP.id);
            onClose();
          }}
          className="w-full py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors border border-red-500/20"
        >
          Delete Participant
        </button>
      </div>
    </div>
  );
};
